import { QueryClient } from "@tanstack/react-query";

/**
 * Single client-side server-state cache for the whole app (D42). Pages read through
 * this instead of fetching in useEffect, so revisiting a page shows cached content
 * instantly and revalidates in the background, and the portal shell never resets.
 *
 * In-memory only on purpose: the portal holds contact + school data, and stale
 * persisted state after reopening the browser would be confusing.
 */
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      gcTime: 30 * 60_000,
      retry: 1,
      // Always revalidate on mount: navigating to a tab serves the cached data instantly
      // (no skeleton) and refetches in the background, so you never need to refresh to see
      // the latest. Cheap here (tiny payloads). Cached observers update in place if changed.
      refetchOnMount: "always",
      refetchOnWindowFocus: true,
      refetchOnReconnect: true,
    },
    mutations: { retry: 0 },
  },
});

/** Disciplined query-key factory so invalidation is predictable. */
export const portalKeys = {
  all: ["portal"] as const,
  mySchools: () => [...portalKeys.all, "my-schools"] as const,
  school: (schoolId: number | string) => [...portalKeys.all, "school", String(schoolId)] as const,
  bootstrap: (schoolId: number | string) => [...portalKeys.school(schoolId), "bootstrap"] as const,
  overview: (schoolId: number | string) => [...portalKeys.school(schoolId), "overview"] as const,
  progress: (schoolId: number | string) => [...portalKeys.school(schoolId), "progress"] as const,
  contacts: (schoolId: number | string) => [...portalKeys.school(schoolId), "contacts"] as const,
  classes: (schoolId: number | string) => [...portalKeys.school(schoolId), "classes"] as const,
  constraints: (schoolId: number | string) => [...portalKeys.school(schoolId), "constraints"] as const,
  selections: (schoolId: number | string) => [...portalKeys.school(schoolId), "selections"] as const,
  eligible: (schoolId: number | string) => [...portalKeys.school(schoolId), "eligible"] as const,
  submissionStatus: (schoolId: number | string) => [...portalKeys.school(schoolId), "submission-status"] as const,
  preflight: (schoolId: number | string) => [...portalKeys.school(schoolId), "preflight"] as const,
};

/** Invalidate every cached query for one school (after a mutation that may change state). */
export function invalidateSchool(schoolId: number | string) {
  return queryClient.invalidateQueries({ queryKey: portalKeys.school(schoolId) });
}

/**
 * Targeted invalidation for a programma SELECTION save. A selection only changes the derived
 * progress (stepper status, next-step) and the rolled-up overview/pending state, NOT the school's
 * contacts/classes/constraints. Crucially it does NOT touch `selections`/`eligible`: the editing
 * section already reloads + re-caches those itself after each save, so invalidating them here only
 * triggered a redundant second refetch on every click. It also does NOT touch `progress`: the shell's
 * settled-save effect calls refreshSections() in the SAME tick, which fetches /progress fresh and
 * writes it straight into the cache — invalidating that key here would only mark the just-written
 * fresh data stale (and risk a redundant refetch if anything later observes it). So this is just the
 * two DERIVED, useQuery-observed reads the shell does not refetch itself: the bootstrap (drives the
 * Controleren delivery state) and the overview (the Overzicht tab). Invalidating the whole school
 * subtree on each selection (all 8 queries, every one with refetchOnMount: "always") was the other
 * half of the save storm that froze the nav; this keeps a settled save to a single, minimal refresh.
 */
export function invalidateSelectionDerived(schoolId: number | string) {
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: portalKeys.overview(schoolId) }),
    queryClient.invalidateQueries({ queryKey: portalKeys.bootstrap(schoolId) }),
  ]);
}

/**
 * Inline-bootstrap hydration (D45): the server embeds the whole portal dataset in the
 * HTML for an authenticated /school/:id/* request as `window.__KUVO_PORTAL__`. We seed
 * the cache from it BEFORE React renders, so a cold load / hard refresh paints with real
 * data and no skeleton. Runs once at module load (this module is imported before render).
 */
declare global {
  interface Window {
    __KUVO_PORTAL__?: { schoolId: number; data: Record<string, unknown> };
  }
}

function hydrateFromInlineBootstrap() {
  if (typeof window === "undefined") return;
  const boot = window.__KUVO_PORTAL__;
  if (!boot?.data) return;
  const id = boot.schoolId;
  const keyFor: Record<string, readonly unknown[]> = {
    bootstrap: portalKeys.bootstrap(id),
    overview: portalKeys.overview(id),
    progress: portalKeys.progress(id),
    contacts: portalKeys.contacts(id),
    classes: portalKeys.classes(id),
    constraints: portalKeys.constraints(id),
    selections: portalKeys.selections(id),
    eligible: portalKeys.eligible(id),
    mySchools: portalKeys.mySchools(),
  };
  for (const [name, key] of Object.entries(keyFor)) {
    if (boot.data[name] !== undefined) queryClient.setQueryData(key, boot.data[name]);
  }
  // The cache now owns this data; don't leave a copy of contact/school details on window.
  delete window.__KUVO_PORTAL__;
}

hydrateFromInlineBootstrap();
